import { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import Papa from "papaparse";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface CheckoutLineItem {
  title: string;
  quantity: number;
  price: number;
}

interface CheckoutRow {
  _id: string;
  status?: string;
  customerEmail?: string;
  firstName?: string;
  lastName?: string;
  lineItems?: CheckoutLineItem[];
  chargeAmountCents?: number;
  createdAt?: string;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function serializeLeaf(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function flattenCheckoutValue({
  prefix,
  value,
  out,
}: {
  prefix: string;
  value: unknown;
  out: Record<string, string>;
}): void {
  if (value === null || value === undefined) {
    if (prefix) out[prefix] = "";
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, i) => {
      flattenCheckoutValue({
        prefix: `${prefix}[${i}]`,
        value: item,
        out,
      });
    });
    return;
  }
  if (isPlainObject(value)) {
    for (const [k, v] of Object.entries(value)) {
      flattenCheckoutValue({
        prefix: prefix ? `${prefix}.${k}` : k,
        value: v,
        out,
      });
    }
    return;
  }
  out[prefix] = serializeLeaf(value);
}

function checkoutsDocumentsToCsv(checkouts: Record<string, unknown>[]): string {
  const keys = new Set<string>();
  const rows = checkouts.map((doc) => {
    const row: Record<string, string> = {};
    flattenCheckoutValue({ prefix: "", value: doc, out: row });
    for (const k of Object.keys(row)) keys.add(k);
    return row;
  });
  const fields = [...keys].sort((a, b) => a.localeCompare(b));
  const data = rows.map((row) => {
    const out: Record<string, string> = {};
    for (const field of fields) {
      out[field] = row[field] ?? "";
    }
    return out;
  });
  return Papa.unparse({ fields, data });
}

function formatDate(dateString?: string) {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatAmount(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function checkoutAmountCents(checkout: CheckoutRow): number | null {
  if (typeof checkout.chargeAmountCents === "number") {
    return checkout.chargeAmountCents;
  }
  const items = checkout.lineItems;
  if (!items?.length) return null;
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

function customerName(checkout: CheckoutRow): string {
  return [checkout.firstName, checkout.lastName].filter(Boolean).join(" ").trim();
}

export function Checkouts() {
  const [checkouts, setCheckouts] = useState<CheckoutRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedOnce = useRef(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/checkouts");
        if (response.ok) {
          const data = await response.json();
          setCheckouts((data.checkouts || []) as CheckoutRow[]);
        } else {
          console.error("Failed to fetch checkouts");
        }

        setBreadcrumbs([
          { label: "Sales", path: "/products" },
          { label: "Checkouts", path: "/sales/checkouts" },
        ]);
      } catch (error) {
        console.error("Failed to fetch checkouts:", error);
      } finally {
        setIsLoading(false);
        hasLoadedOnce.current = true;
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, [setBreadcrumbs, clearBreadcrumbs]);

  const handleExportCsv = () => {
    if (checkouts.length === 0) return;
    const csv = checkoutsDocumentsToCsv(checkouts as unknown as Record<string, unknown>[]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `checkouts-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading && !hasLoadedOnce.current) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Checkouts</h1>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleExportCsv}
          disabled={checkouts.length === 0}
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Items</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead className="hidden lg:table-cell">Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              </TableCell>
            </TableRow>
          ) : checkouts.length > 0 ? (
            checkouts.map((checkout) => {
              const amount = checkoutAmountCents(checkout);
              const name = customerName(checkout);
              return (
                <TableRow key={checkout._id}>
                  <TableCell className="capitalize">{checkout.status || "—"}</TableCell>
                  <TableCell>
                    {checkout.customerEmail || "—"}
                  </TableCell>
                  <TableCell>{name || "—"}</TableCell>
                  <TableCell className="max-w-[280px] text-sm whitespace-normal">
                    {!checkout.lineItems?.length ? (
                      "—"
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        {checkout.lineItems.map((line, idx) => {
                          const qty =
                            line.quantity != null && line.quantity > 1
                              ? ` ×${line.quantity}`
                              : "";
                          return (
                            <div key={`${checkout._id}-${idx}`}>
                              {line.title}
                              {qty ? (
                                <span className="text-muted-foreground">{qty}</span>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {amount == null ? "—" : formatAmount(amount)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {formatDate(checkout.createdAt)}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                No checkouts yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
